/**
 * GlobalView — Vessel Detail Popup
 *
 * Overlay showing details for the selected ship/vessel.
 * Appears when a ship entity is selected, dismisses on close or ESC.
 * On mobile: renders as a bottom sheet with minimize/expand.
 */

import { useEffect, useRef } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { getVesselCategoryColor, getVesselCategoryLabel } from '../../services/layers/vesselIcons';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import EntityBottomSheet from './EntityBottomSheet';
import type { ShipProperties } from '@globalview/shared';
import styles from './VesselPopup.module.css';

// ─── Formatters ──────────────────────────────────────────────────

function fmtCoord(val: number, decimals = 5): string {
  return val.toFixed(decimals);
}

function fmtSpeed(sog: number): string {
  return `${sog.toFixed(1)} kn`;
}

function fmtCourse(deg: number): string {
  return `${deg.toFixed(1)}°`;
}

function timeAgo(isoStr: string): string {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 5) return 'jetzt';
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  return `vor ${Math.round(diff / 60)}min`;
}

// ─── Nav Status Badge ────────────────────────────────────────────

function getNavBadgeClass(navStatus: number): string {
  if (navStatus === 0 || navStatus === 8) return styles.underway;
  if (navStatus === 5) return styles.moored;
  if (navStatus === 1) return styles.anchored;
  return styles.other;
}

// ─── Shared Content ──────────────────────────────────────────────

interface VesselContentProps {
  entity: { position: { lat: number; lon: number }; source: string; timestamp: string };
  props: ShipProperties;
}

function VesselContent({ entity, props }: VesselContentProps) {
  const mmsi = props.mmsi ?? '—';
  const callSign = props.callSign ?? '—';
  const destination = props.destination ?? '—';
  const imo = props.imo ?? '—';
  const sog = props.sog ?? 0;
  const cog = props.cog ?? 0;
  const navStatus = props.navStatus ?? 15;
  const navStatusText = props.navStatusText ?? 'Unknown';
  const shipType = props.shipType ?? 'Unknown';

  // Dimensions
  const dimA = props.dimensionA ?? 0;
  const dimB = props.dimensionB ?? 0;
  const length = dimA + dimB;

  return (
    <>
      {/* Nav status badge */}
      <div className={`${styles.navBadge} ${getNavBadgeClass(navStatus)}`}>
        {navStatusText.toUpperCase()}
      </div>

      {/* Dynamic */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>DYNAMIK</div>
        <div className={styles.grid}>
          <Row label="SOG" value={fmtSpeed(sog)} />
          <Row label="COG" value={fmtCourse(cog)} />
          {destination !== '—' && <Row label="ZIEL" value={destination} />}
        </div>
      </div>

      {/* Position */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>POSITION</div>
        <div className={styles.grid}>
          <Row label="LAT" value={`${fmtCoord(entity.position.lat)}°`} />
          <Row label="LON" value={`${fmtCoord(entity.position.lon)}°`} />
        </div>
      </div>

      {/* Info */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>INFO</div>
        <div className={styles.grid}>
          <Row label="TYP" value={getVesselCategoryLabel(shipType)} />
          <Row label="MMSI" value={mmsi} />
          {imo !== '—' && <Row label="IMO" value={imo} />}
          {callSign !== '—' && <Row label="CALL" value={callSign} />}
          {length > 0 && <Row label="L" value={`${length}m`} />}
          <Row label="SRC" value={entity.source.toUpperCase()} />
          <Row label="UPD" value={timeAgo(entity.timestamp)} />
        </div>
      </div>
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────

export default function VesselPopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  // Reset position when a new vessel is selected
  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  // ESC key to close
  useEffect(() => {
    if (!selectedEntityId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedEntity(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  // Only render for ship entities
  if (!selectedEntityId || !entity || entity.type !== 'ship') return null;

  const props = entity.properties as unknown as ShipProperties;
  const vesselName = props.name || props.mmsi || '—';
  const shipType = props.shipType ?? 'Unknown';
  const categoryColor = getVesselCategoryColor(shipType);

  const handleClose = () => {
    setSelectedEntity(null);
  };

  // Desktop popup
  const desktopPopup = (
    <div
      className={styles.popup}
      style={dragStyle}
      id="vessel-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header — drag handle */}
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.vesselName} style={{ color: categoryColor }}>
            {vesselName}
          </span>
          <span className={styles.shipType}>
            {getVesselCategoryLabel(shipType)}
          </span>
        </div>
        <button
          className={styles.closeBtn}
          onClick={handleClose}
          title="Schliessen (ESC)"
          id="vessel-popup-close"
        >
          ✕
        </button>
      </div>
      <VesselContent entity={entity} props={props} />
    </div>
  );

  // Mobile bottom sheet wrapping
  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={vesselName}
        entitySub={props.destination ? `→ ${props.destination}` : getVesselCategoryLabel(shipType)}
        icon="🚢"
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        <VesselContent entity={entity} props={props} />
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{value}</span>
    </div>
  );
}
